import { formatInTimeZone } from "date-fns-tz";

import { HistoricalTrends, TrendChart } from "entities/assets";
import { ChartRefLine, ChartSeries } from "modules/assets/HistoricalChart";
import { ChartCaptionSegment, HistoricalChartData } from "modules/assets/HistoricalChartCard";

const GST_ZONE = "Asia/Dubai";

export type PeriodKey = "24h" | "7d" | "30d";

export type PeriodMeta = {
  key: PeriodKey;
  label: string;
  interval: string;
  peakLabel: string;
};

export const PERIODS: PeriodMeta[] = [
  { key: "24h", label: "24 hours", interval: "30-minute", peakLabel: "30-min peak" },
  { key: "7d", label: "7 days", interval: "6-hour", peakLabel: "6-hour peak" },
  { key: "30d", label: "30 days", interval: "daily", peakLabel: "Daily peak" },
];

export const DEFAULT_PERIOD_LABEL = "7 days";

export const buildBanner = (interval: string) =>
  `Each data point = peak reading within a ${interval} interval. One line represents one device. ` +
  `Dashed lines mark the applicable limit for this metric per DM-HSD-GU119 — see the label below each chart for details.`;

export const AIR_QUALITY_ORDER = ["co2", "hcho", "tvoc", "pm2_5", "pm10"];
export const THERMAL_ORDER = ["temperature", "humidity"];

const METRIC_TITLE: Record<string, string> = {
  co2: "CO₂ lvl",
  hcho: "HCHO lvl",
  tvoc: "TVOC lvl",
  pm2_5: "PM2.5 lvl",
  pm10: "PM10 lvl",
  temperature: "Air temp lvl",
  humidity: "Humidity lvl",
};

const METRIC_INFO: Record<string, string> = {
  tvoc: "Approximate conversion (MOX sensor)",
};

const METRIC_NDIGITS: Record<string, number> = {
  co2: 0,
  hcho: 3,
  tvoc: 0,
  pm2_5: 0,
  pm10: 0,
  temperature: 1,
  humidity: 1,
};

// Design palette; a line's colour is unique within a period tab. When there are
// more device-lines than palette entries, colours repeat (still deterministic).
const PALETTE = [
  "#0a86e3",
  "#e64dd8",
  "#f5b700",
  "#00c389",
  "#7a5af8",
  "#ff6b6b",
  "#12b0a0",
  "#8d5524",
  "#3d5afe",
  "#c2185b",
  "#00acc1",
  "#7cb342",
];

const stripZeros = (text: string) => (text.includes(".") ? text.replace(/\.?0+$/, "") : text);

const makeFormat = (ndigits: number) => (value: number) => stripZeros(value.toFixed(ndigits));

function niceStep(span: number): number {
  const rough = span / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return niceNorm * mag;
}

export function niceRange(rawMin: number, rawMax: number): { min: number; max: number; ticks: number[] } {
  if (!(rawMax > rawMin)) return { min: 0, max: 1, ticks: [1, 0.75, 0.5, 0.25, 0] };
  const step = niceStep(rawMax - rawMin);
  const min = Math.floor(rawMin / step + 1e-9) * step;
  const max = Math.ceil(rawMax / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let v = max; v >= min - 1e-9; v -= step) ticks.push(Math.round((v / step)) * step);
  return { min, max, ticks };
}

export function niceScale(rawMax: number): { max: number; ticks: number[] } {
  const range = niceRange(0, rawMax > 0 ? rawMax : 1);
  return { max: range.max, ticks: range.ticks };
}

const refValue = (chart: TrendChart, type: string): number | undefined =>
  chart.reference_lines.find((line) => line.type === type)?.value;

function assignColors(trends: HistoricalTrends): Map<string, string> {
  const colors = new Map<string, string>();
  let next = 0;
  const assign = (metric: string, deviceId: number) => {
    const key = `${metric}:${deviceId}`;
    if (!colors.has(key)) {
      colors.set(key, PALETTE[next % PALETTE.length]);
      next += 1;
    }
  };
  for (const chart of [...trends.air_quality, ...trends.thermal_comfort]) {
    for (const series of chart.series) assign(chart.metric, series.device_id);
    for (const sensor of chart.sensors ?? []) assign(chart.metric, sensor.device_id);
  }
  return colors;
}

function pointTimesFor(period: PeriodKey, buckets: string[], bucketSeconds: number): string[] {
  return buckets.map((iso) => {
    const start = new Date(iso);
    const end = new Date(start.getTime() + bucketSeconds * 1000);
    if (period === "24h") return formatInTimeZone(end, GST_ZONE, "HH:mm");
    if (period === "7d") return formatInTimeZone(end, GST_ZONE, "d MMM, HH:mm");
    return formatInTimeZone(start, GST_ZONE, "d MMM");
  });
}

function xLabelsFor(period: PeriodKey, buckets: string[]): { text: string; at: number }[] {
  const count = buckets.length;
  if (count === 0) return [];

  if (period === "30d") {
    const lastStart = new Date(buckets[count - 1]).getTime();
    const labels: { text: string; at: number }[] = [];
    for (let back = 30; back >= 5; back -= 5) {
      const index = count - 1 - back;
      labels.push({
        text: formatInTimeZone(new Date(lastStart - back * 86_400_000), GST_ZONE, "d MMM"),
        at: count <= 1 ? 0 : Math.max(0, index / (count - 1)),
      });
    }
    return labels;
  }

  const step = period === "24h" ? 8 : 4;
  const fmt = period === "24h" ? "H:mm" : "d MMM";
  const labels: { text: string; at: number }[] = [];
  for (let index = 0; index < count; index += step) {
    labels.push({
      text: formatInTimeZone(new Date(buckets[index]), GST_ZONE, fmt),
      at: count <= 1 ? 0 : index / (count - 1),
    });
  }
  return labels;
}

function captionsFor(
  metric: string,
  chart: TrendChart,
  threshold: number | null,
  unit: string,
  format: (v: number) => string,
): { captions: ChartCaptionSegment[][]; thresholdNote?: string } {
  if (metric === "pm2_5" || metric === "pm10") {
    return { captions: [[{ text: "According to DM-HSD-GU119, there is no instant threshold value" }]] };
  }

  if (metric === "temperature" || metric === "humidity") {
    const min = refValue(chart, "recommended_min");
    const max = refValue(chart, "recommended_max");
    const range: ChartCaptionSegment[] = [
      { text: "Recommended range " },
      { text: `${min ?? "-"}-${max ?? "-"} ${unit}`, tone: "cyan" },
    ];
    if (metric === "humidity") {
      const c19 = refValue(chart, "ceiling_19c");
      const c27 = refValue(chart, "ceiling_27c");
      return {
        captions: [
          range,
          [
            { text: "Never to exceed " },
            { text: `${c27 ?? "-"}% at 27°C`, tone: "red" },
            { text: " and " },
            { text: `${c19 ?? "-"}% at 19°C`, tone: "red" },
          ],
        ],
      };
    }
    return { captions: [range] };
  }

  if (threshold != null) {
    const note = `Threshold limit ${format(threshold)} ${unit}`;
    return {
      captions: [[{ text: "Threshold limit " }, { text: `${format(threshold)} ${unit}`, tone: "red" }]],
      thresholdNote: note,
    };
  }
  return { captions: [] };
}

function refLinesFor(chart: TrendChart, threshold: number | null): ChartRefLine[] {
  const lines: ChartRefLine[] = [];
  if (threshold != null) lines.push({ kind: "h", y: threshold, color: "red" });

  const min = refValue(chart, "recommended_min");
  const max = refValue(chart, "recommended_max");
  if (min != null) lines.push({ kind: "h", y: min, color: "cyan" });
  if (max != null) lines.push({ kind: "h", y: max, color: "cyan" });

  if (chart.ceiling_line && chart.ceiling_line.some((v) => v != null)) {
    lines.push({ kind: "wave", values: chart.ceiling_line, color: "red" });
  }
  return lines;
}

function buildChart(
  chart: TrendChart,
  meta: PeriodMeta,
  buckets: string[],
  pointTimes: string[],
  xLabels: { text: string; at: number }[],
  colors: Map<string, string>,
): HistoricalChartData {
  const metric = chart.metric;
  const ndigits = METRIC_NDIGITS[metric] ?? 1;
  const format = makeFormat(ndigits);
  const threshold = refValue(chart, "threshold") ?? null;

  const series: ChartSeries[] = chart.series.map((item) => ({
    color: colors.get(`${metric}:${item.device_id}`) ?? PALETTE[0],
    deviceId: item.dev_eui,
    location: item.location || "-",
    values: item.points,
  }));

  const sensors = (chart.sensors ?? []).map((item) => ({
    color: colors.get(`${metric}:${item.device_id}`) ?? PALETTE[0],
    deviceId: item.dev_eui,
    location: item.location || "-",
  }));

  const refLines = refLinesFor(chart, threshold);
  const dataValues = series.flatMap((s) => s.values).filter((v): v is number => v != null);
  // Only the lines actually drawn (recommended min/max, threshold, ceiling wave)
  // widen the axis — the constant ceiling_19c/27c refs feed the caption only.
  const refLineValues = refLines.flatMap((line) =>
    line.kind === "h" ? [line.y] : line.values.filter((v): v is number => v != null),
  );

  let yMin: number;
  let yMax: number;
  let yTicks: number[];
  if (metric === "temperature") {
    // Temperature can be negative, so both bounds are dynamic (FR 11.5 §3),
    // clamped to the recommended range and padded by ~15% of the span.
    const all = [...dataValues, ...refLineValues];
    const lo = all.length ? Math.min(...all) : 0;
    const hi = all.length ? Math.max(...all) : 1;
    const pad = (hi - lo || 1) * 0.15;
    ({ min: yMin, max: yMax, ticks: yTicks } = niceRange(lo - pad, hi + pad));
  } else {
    // Min always 0; max covers data + drawn limits/ceiling, +10% headroom.
    const base = Math.max(0, ...dataValues, ...refLineValues);
    ({ min: yMin, max: yMax, ticks: yTicks } = niceRange(0, base > 0 ? base * 1.1 : 1));
  }

  const { captions, thresholdNote } = captionsFor(metric, chart, threshold, chart.unit, format);

  const emptyState = !chart.has_sensors ? "no-sensors" : series.length === 0 ? "no-data" : null;

  return {
    id: metric,
    title: METRIC_TITLE[metric] ?? metric,
    unit: chart.unit,
    info: METRIC_INFO[metric],
    badge: meta.peakLabel,
    emptyState,
    series,
    sensors,
    yMin,
    yMax,
    yTicks,
    refLines,
    threshold,
    captions,
    thresholdNote,
    valueUnit: chart.unit,
    pointTimes,
    xLabels,
    peakLabel: meta.peakLabel,
    format,
  };
}

export function buildCharts(
  trends: HistoricalTrends,
  meta: PeriodMeta,
): { airQuality: HistoricalChartData[]; thermal: HistoricalChartData[] } {
  const colors = assignColors(trends);
  const pointTimes = pointTimesFor(meta.key, trends.buckets, trends.bucket_seconds);
  const xLabels = xLabelsFor(meta.key, trends.buckets);
  const build = (chart: TrendChart) => buildChart(chart, meta, trends.buckets, pointTimes, xLabels, colors);
  return {
    airQuality: trends.air_quality.map(build),
    thermal: trends.thermal_comfort.map(build),
  };
}
